/**
 * Service worker registration and update handling.
 *
 * The worker does two jobs that must not be separated: it caches the shell so
 * the installed app opens instantly and degrades honestly offline, *and* it
 * receives Web Push. A second worker would fight this one for the root scope,
 * which is why there is exactly one — see public/sw.js.
 *
 * Updates are handled rather than ignored. A single-page app that has been
 * open on a phone for a week is otherwise running last week's code with this
 * week's database, and the failure looks like a bug rather than staleness.
 */

const SW_URL = '/sw.js';

type UpdateListener = () => void;
const updateListeners = new Set<UpdateListener>();

let waitingWorker: ServiceWorker | null = null;

/** Notified when a new version is downloaded and waiting to take over. */
export function onUpdateReady(listener: UpdateListener): () => void {
  updateListeners.add(listener);
  if (waitingWorker) listener();
  return () => updateListeners.delete(listener);
}

export function hasPendingUpdate(): boolean {
  return waitingWorker !== null;
}

/**
 * Apply a downloaded update.
 *
 * Reload is driven by `controllerchange` rather than fired immediately, so the
 * page never reloads before the new worker has actually taken control — which
 * would just reload into the old one.
 */
export function applyUpdate(): void {
  if (!waitingWorker) {
    window.location.reload();
    return;
  }
  waitingWorker.postMessage({ type: 'SKIP_WAITING' });
}

export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;
  // A worker in dev intercepts module requests and makes hot reload lie.
  if (import.meta.env.DEV) return;

  window.addEventListener('load', () => {
    void navigator.serviceWorker
      .register(SW_URL, { scope: '/' })
      .then((registration) => {
        if (registration.waiting) {
          waitingWorker = registration.waiting;
          updateListeners.forEach((fn) => fn());
        }

        registration.addEventListener('updatefound', () => {
          const installing = registration.installing;
          if (!installing) return;
          installing.addEventListener('statechange', () => {
            // `controller` is null on the very first install — that is not an
            // update, it is the app becoming offline-capable for the first
            // time, and prompting for it would be nonsense.
            if (installing.state === 'installed' && navigator.serviceWorker.controller) {
              waitingWorker = installing;
              updateListeners.forEach((fn) => fn());
            }
          });
        });
      })
      .catch(() => {
        // No worker means no offline shell and no push. Everything else works.
      });

    let reloading = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (reloading) return;
      reloading = true;
      window.location.reload();
    });

    /*
     * Check for a new version whenever the app is returned to, and take it.
     *
     * An installed PWA can sit on a home screen for weeks. Relying on somebody
     * noticing a "Reload" banner means a phone can keep running last week's
     * code against this week's database and security rules — which is not a
     * cosmetic staleness problem, it is the app appearing broken in ways that
     * cannot be reproduced anywhere else.
     *
     * Coming back to a backgrounded app is the one moment a reload costs
     * nothing: the reader is not mid-sentence, and the app restores to the
     * same route. So the update is applied then, silently. The banner stays
     * for the case where they are actively using it and a version lands.
     */
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') return;

      void navigator.serviceWorker.getRegistration().then((registration) => {
        if (!registration) return;
        // A waiting worker is one we already have; take it now.
        if (registration.waiting) {
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
          return;
        }
        // Otherwise ask whether there is one, for next time.
        void registration.update().catch(() => {});
      });
    });
  });
}

/**
 * True when running as an installed app rather than in a browser tab.
 *
 * iOS reports this through a non-standard `navigator.standalone`; everyone
 * else through the display-mode media query. Both are checked because the
 * install onboarding is aimed squarely at iPhone.
 */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const iosStandalone = (window.navigator as { standalone?: boolean }).standalone === true;
  return iosStandalone || window.matchMedia('(display-mode: standalone)').matches;
}

export function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  // iPadOS 13+ reports itself as a Mac; the touch-point count gives it away.
  return /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}
